import type { PropsWithChildren } from 'react';
import { useEffect } from 'react';
import { useAuthStore } from '@gov-nx/store';
import { useSessionStorage } from '@gov-nx/utils/web';
import { useExtraConfigContext } from '@gov-nx/core/app';
import { useTabTracking } from './useTabTracking';
import { useLatest } from '@gov-nx/core/hooks';

export const TabTrackingProvider = ({ children }: PropsWithChildren) => {
	const token = useAuthStore('token');
	const setAccessToken = useAuthStore('setAccessToken');
	const { removeActiveTab, updateActiveTabCount } = useTabTracking();
	const sessionAuthToken = useSessionStorage('sessionAuthToken');
	const { extraConfig } = useExtraConfigContext();

	useEffect(() => {
		// Restore token saved by onLastTabRemoved on hard reload. The presence of
		// sessionAuthToken is the one-shot signal — it is only ever written right
		// before a reload and consumed here, so every reload cycle restores cleanly.
		if (sessionAuthToken.value) {
			setAccessToken(sessionAuthToken.value);
			sessionAuthToken.remove();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const tokenRef = useLatest(token);

	useEffect(() => {
		const onStorageChange = (event: StorageEvent) => {
			if (event.key === 'logoutTimeStamp') {
				window.location.reload();
			}
		};
		window.addEventListener('storage', onStorageChange);
		return () => window.removeEventListener('storage', onStorageChange);
	}, []);

	useEffect(() => {
		const onPageHide = (event: PageTransitionEvent) => {
			// Tab is entering back/forward cache — still alive, do nothing
			if (event.persisted) return;

			removeActiveTab({
				onLastTabRemoved: () => {
					if (tokenRef.current) {
						// Survives hard reload via sessionStorage, ignored on tab close
						sessionAuthToken.set(tokenRef.current);

						// Must write directly to localStorage — setAccessToken(undefined)
						// only updates in-memory state, and the async persist flush won't
						// complete before the tab is killed
						const authStoreKey = `${extraConfig.store.persisKey}:auth`;
						try {
							const stored = localStorage.getItem(authStoreKey);
							if (stored) {
								const parsed = JSON.parse(stored);
								parsed.state.token = undefined;
								localStorage.setItem(authStoreKey, JSON.stringify(parsed));
							}
						} catch {
							// Wipe the entry on error so the user is never silently left logged in
							localStorage.removeItem(authStoreKey);
						}

						setAccessToken(undefined);
					}
				},
			});
		};

		const onPageShow = (event: PageTransitionEvent) => {
			// Re-register tab after bfcache restore — entry may have been pruned
			if (event.persisted) {
				updateActiveTabCount();
			}
		};

		// Refresh heartbeat whenever this tab returns to the foreground so it is
		// never treated as stale by another tab closing while this one is live.
		// This covers two scenarios that pagehide/pageshow alone cannot catch:
		//   • visibilitychange: tab switched away and back (timer-throttled while hidden)
		//   • focus: window re-focused without a full hide/show cycle
		const onVisible = () => {
			if (document.visibilityState === 'visible') {
				updateActiveTabCount();
			}
		};

		const onFocus = () => {
			updateActiveTabCount();
		};

		window.addEventListener('pagehide', onPageHide);
		window.addEventListener('pageshow', onPageShow);
		document.addEventListener('visibilitychange', onVisible);
		window.addEventListener('focus', onFocus);
		return () => {
			window.removeEventListener('pagehide', onPageHide);
			window.removeEventListener('pageshow', onPageShow);
			document.removeEventListener('visibilitychange', onVisible);
			window.removeEventListener('focus', onFocus);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return children;
};
